/**
 * Create by zhuzhaoqing
 * Description: index
 * Date: 2023/3/31 10:22
 **/
import router from '@/router'
import { defineStore } from 'pinia'
import { setFrontUrlFetch, ssoLoginFetch, ssoLogoutFetch } from '@/api/login'
import { awaitWrap, reportEvent, reportInit } from '@/common'

// 定义更详细的类型
interface MenuItem {
  menuId?: string
  parentId?: string
  menuAdr?: string
  menuName?: string
  children?: MenuItem[]
  icon?: string
}

interface UserState {
  userInfo: string
  parsedUserInfo: any | null
  isCollapsed: boolean
  cachedName: string[]
  NoticeInfo: any
  menuData: MenuItem[]
}

// 静态路由名称
const staticRoutes = [
  'goodsSettingDetail',
  'thirdPartyBillDetail',
  'systemNotificationDetail',
  'notificationList',
  'promotionCodeAdd',
  'reconciliationDetail',
]

export const useStore = defineStore('main', {
  state: (): UserState => ({
    userInfo: sessionStorage.getItem('userInfo') || '{"menu":[]}',
    parsedUserInfo: null,
    isCollapsed: false,
    cachedName: [],
    NoticeInfo: undefined,
    menuData: [],
  }),

  getters: {
    getParsedUserInfo(): any | null {
      if (!this.parsedUserInfo && this.userInfo) {
        try {
          this.parsedUserInfo = JSON.parse(this.userInfo)
        } catch (e) {
          console.error('Failed to parse userInfo:', e)
          this.parsedUserInfo = null
        }
      }
      return this.parsedUserInfo
    },
  },

  actions: {
    handleMenu(menuStr: string): MenuItem[] {
      const menu = JSON.parse(menuStr)?.menu || []
      let parentMenu: MenuItem[] = []

      menu?.forEach((item: MenuItem) => {
        if (!item.parentId) {
          parentMenu.push({ ...item, children: [], icon: item.icon ? item.icon : 'sys' })
        }
      })

      menu?.forEach((item: MenuItem) => {
        parentMenu.forEach((parentItem) => {
          if (item.parentId === parentItem.menuId) {
            parentItem.children?.push(item)
          }
        })
      })

      // 过滤掉没有子菜单的父级菜单
      parentMenu = parentMenu.filter((item) => item.children?.length)

      return parentMenu
    },

    setCachedName(n: string[]) {
      this.cachedName = n
    },

    setNoticeInfo(obj: any) {
      this.NoticeInfo = obj
    },

    setUserInfo(info: string) {
      this.userInfo = info
      sessionStorage.setItem('userInfo', info)
      try {
        this.parsedUserInfo = JSON.parse(info)
      } catch (e) {
        console.error('Failed to parse userInfo:', e)
        this.parsedUserInfo = null
      }
    },

    clearUserInfo() {
      this.userInfo = '{"menu":[]}'
      this.parsedUserInfo = null
      sessionStorage.removeItem('userInfo')
    },

    async ssoLogin() {
      if (process.env['NODE_ENV'] === 'development' && process.env['VUE_APP_ISJOINT'] === 'true') {
        const param = {
          frontUrl: `${process.env.VUE_APP_FRONT_URL}`,
          frontErrUrl: `${process.env.VUE_APP_FRONT_ERRURL}`,
          frontLogoutUrl: `${process.env.VUE_APP_FRONT_LOGOUTURL}`,
        }
        const [err, data] = await awaitWrap(setFrontUrlFetch(param))
        if (data) await this.ssoLoginAfter()
        if (err) console.log('%c[index.ts] - 55', 'padding:2px;border-radius:4px;background:lightcoral;color:#fff', err)
      } else {
        await this.ssoLoginAfter()
      }
    },

    async ssoLoginAfter() {
      try {
        const { code, activeUserInfo } = await ssoLoginFetch()
        if (code === '0000') {
          await reportInit(activeUserInfo)

          if (activeUserInfo.menu?.length) {
            this.setUserInfo(JSON.stringify(activeUserInfo))
            this.menuData = this.handleMenu(JSON.stringify(activeUserInfo))
            const menuNames = activeUserInfo.menu.map((item: MenuItem) => item.menuAdr)?.concat(staticRoutes)
            const currentPath = window.location.hash.split('#')[1]?.split('/')[1]?.split('?')[0]
            const isDev = process.env.NODE_ENV === 'development'

            if (!isDev && !menuNames.includes(currentPath)) {
              router.push({
                name: this.menuData?.[0]?.children?.[0]?.menuAdr,
              })
            }
            const menuName = activeUserInfo?.menu?.find((item: MenuItem) => item.menuAdr === currentPath)?.menuName
            if (menuName) {
              await reportEvent({
                eventName: 'ui_open',
                properties: {
                  ui_name: menuName + '页面打开',
                },
              })
            }
          } else {
            router.push({
              name: 'error',
              query: { code: '9001' },
            })
            await reportEvent({
              eventName: 'ui_open',
              properties: {
                ui_name: 'error页面打开',
              },
            })
          }
        }
      } catch (e) {
        console.log(e)
      }
    },

    async ssoLogout() {
      try {
        this.clearUserInfo()
        await ssoLogoutFetch()
        await this.ssoLogin()
      } catch (e) {
        console.log(e)
      }
    },

    changeCol() {
      this.isCollapsed = !this.isCollapsed
    },

    // 刷新用户权限
    async refreshUserPermissions() {
      try {
        const { code, activeUserInfo } = await ssoLoginFetch()
        if (code === '0000' && activeUserInfo) {
          this.setUserInfo(JSON.stringify(activeUserInfo))
          return true
        }
        return false
      } catch (e) {
        console.error('Failed to refresh permissions:', e)
        return false
      }
    },
  },
})
